import React, {
    useCallback,
    useEffect,
    useId,
    useInsertionEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import Spinner from './Spinner';
import { CONTROL_TEXT_SIZE, CONTROL_TEXT_SIZE_CLASS } from './controlTextSize';
import { pushEscapeLayer } from './escapeLayers';
import { ensureMultiSelectPanelStyles } from './optionRowStyles';
import { overlayIdentity } from './portalOverlay';
import { type CaretMenuGeometry, computeCaretMenuGeometry } from './queryInputGeometry';

export type QueryTokenKind =
    | 'key'
    | 'value'
    | 'operator'
    | 'negation'
    | 'punctuation'
    | 'error'
    | 'plain';

export interface QueryToken {
    /** Index into `value`. */
    start: number;
    /** Exclusive. */
    end: number;
    kind: QueryTokenKind;
}

export interface QuerySuggestion {
    id: string;
    label: string;
    /** Secondary text, right-aligned in the row. */
    detail?: string;
    /** What replaces `activeRange` when accepted. Defaults to `label`. */
    insertText?: string;
}

export type QueryInputSize = 'small' | 'medium' | 'large';

export interface QueryInputProps {
    value: string;
    onChange: (value: string) => void;
    /** Ranges to colour. Tokens that are out of range, reversed or overlapping are ignored. */
    tokens?: QueryToken[];
    /** Rows for the caret menu. Empty or undefined means the menu stays closed. */
    suggestions?: QuerySuggestion[];
    /** The span an accepted suggestion replaces. Defaults to a zero-width span at the caret. */
    activeRange?: { start: number; end: number };
    onAccept?: (suggestion: QuerySuggestion) => void;
    /** Every value change AND every caret move, so the consumer can recompute tokens/suggestions. */
    onQueryContextChange?: (context: { value: string; caret: number }) => void;
    /** Enter with nothing highlighted in the menu. */
    onSubmit?: (value: string) => void;
    placeholder?: string;
    size?: QueryInputSize;
    disabled?: boolean;
    /** Suggestions are being fetched: the menu shows a loading row instead of the empty message. */
    loading?: boolean;
    emptyMessage?: string;
    className?: string;
    style?: React.CSSProperties;
    'aria-label'?: string;
    'aria-labelledby'?: string;
}

// Same 28/32/42 ladder as Search, minus the icon gap this component has no icon to space.
const SIZE_CLASSES: Record<QueryInputSize, string> = {
    small: 'h-[var(--malachite-control-height-small,28px)] px-[10px] rounded-[var(--malachite-radius-10,10px)]',
    medium: 'h-[var(--malachite-control-height-medium,32px)] px-[10px] rounded-xl',
    large: 'h-[var(--malachite-control-height-large,42px)] px-[12px] rounded-[var(--malachite-radius-xxl,14px)]',
};

const TOKEN_COLORS: Record<QueryTokenKind, string> = {
    key: 'var(--malachite-codeblock-keyword, rgb(12, 88, 174))',
    value: 'var(--malachite-codeblock-string, rgb(2, 122, 66))',
    operator: 'var(--malachite-codeblock-type, rgb(12, 88, 174))',
    negation: 'var(--malachite-text-destructive, rgb(237, 53, 54))',
    punctuation: 'var(--malachite-codeblock-foreground, rgb(10, 10, 10))',
    error: 'var(--malachite-text-error, rgb(237, 53, 54))',
    plain: 'var(--malachite-text-primary, rgb(10, 10, 10))',
};

// The one place the mirror and the input agree on typography: diverge here and the coloured
// text drifts out from under the caret it's supposed to sit beneath.
const TEXT_STYLE: Record<QueryInputSize, React.CSSProperties> = {
    small: {
        fontFamily: 'inherit',
        fontSize: CONTROL_TEXT_SIZE.small,
        fontWeight: 500,
        letterSpacing: 'normal',
        lineHeight: 'normal',
        padding: 0,
    },
    medium: {
        fontFamily: 'inherit',
        fontSize: CONTROL_TEXT_SIZE.medium,
        fontWeight: 500,
        letterSpacing: 'normal',
        lineHeight: 'normal',
        padding: 0,
    },
    large: {
        fontFamily: 'inherit',
        fontSize: CONTROL_TEXT_SIZE.large,
        fontWeight: 500,
        letterSpacing: 'normal',
        lineHeight: 'normal',
        padding: 0,
    },
};

/** Before layout the portal hasn't painted yet, so the first geometry pass needs a width. */
const MENU_FALLBACK_WIDTH_PX = 220;

const MENU_MAX_WIDTH_PX = 360;

const MENU_MESSAGE_STYLE: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '10px 12px',
    color: 'var(--malachite-text-dimmed, rgb(161, 161, 161))',
    whiteSpace: 'nowrap',
};

/** Keeps focus (and the caret) on the input so a row click never blurs it first. */
const handleMenuMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
};

interface QuerySegment {
    start: number;
    end: number;
    kind: QueryTokenKind;
}

/** Drops tokens the consumer got wrong rather than letting them corrupt the render. */
const sanitizeTokens = (value: string, tokens: QueryToken[]): QueryToken[] => {
    const inRange = tokens.filter(
        (token) => token.start >= 0 && token.end <= value.length && token.start < token.end,
    );
    const sorted = [...inRange].sort((a, b) => a.start - b.start);
    const accepted: QueryToken[] = [];
    let cursor = 0;
    for (const token of sorted) {
        if (token.start < cursor) {
            continue;
        }
        accepted.push(token);
        cursor = token.end;
    }
    return accepted;
};

/** Fills the gaps between tokens with 'plain' segments so the mirror's text always equals `value`. */
const buildSegments = (value: string, tokens: QueryToken[]): QuerySegment[] => {
    const segments: QuerySegment[] = [];
    let cursor = 0;
    for (const token of sanitizeTokens(value, tokens)) {
        if (token.start > cursor) {
            segments.push({ start: cursor, end: token.start, kind: 'plain' });
        }
        segments.push(token);
        cursor = token.end;
    }
    if (cursor < value.length) {
        segments.push({ start: cursor, end: value.length, kind: 'plain' });
    }
    return segments;
};

const QueryInput: React.FC<QueryInputProps> = ({
    value,
    onChange,
    tokens = [],
    suggestions,
    activeRange,
    onAccept,
    onQueryContextChange,
    onSubmit,
    placeholder,
    size = 'large',
    disabled = false,
    loading = false,
    emptyMessage,
    className = '',
    style,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
}) => {
    const instanceId = useId();
    const listboxId = `${instanceId}-listbox`;
    const getRowId = (index: number) => `${listboxId}-option-${index}`;

    const [focused, setFocused] = useState(false);
    const [caret, setCaret] = useState(() => value.length);
    const [rawHighlightedIndex, setHighlightedIndex] = useState(0);
    // Escape closes the menu whatever `suggestions` and `loading` say. Lifted by an edit or a
    // refocus, so navigating a dismissed query does not pop it back open.
    const [dismissed, setDismissed] = useState(false);
    const [menuPosition, setMenuPosition] = useState<CaretMenuGeometry | null>(null);

    const inputRef = useRef<HTMLInputElement>(null);
    const mirrorRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLSpanElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);
    // Caret restore after an accepted suggestion. Carries the string it was computed against:
    // nothing obliges a controlled consumer to apply the value handed to `onChange`, and a
    // caret from a rejected or rewritten suggestion must not land on a later unrelated edit.
    const pendingSelectionRef = useRef<{ value: string; caret: number } | null>(null);

    const suggestionsProvided = Array.isArray(suggestions);
    const suggestionList = suggestions ?? [];
    const hasSuggestions = suggestionList.length > 0;
    const isEmptyList = suggestionsProvided && !hasSuggestions;
    const wantsMenu = loading || hasSuggestions || (isEmptyList && !!emptyMessage);
    const isMenuOpen = focused && wantsMenu && !dismissed && !disabled;
    // `loading` paints the spinner over any rows the consumer is still holding, so nothing is
    // on screen to arrow through, accept, or point `aria-activedescendant` at.
    const rowsVisible = isMenuOpen && hasSuggestions && !loading;

    // A consumer's `suggestions` array is commonly rebuilt inline, so its identity changes on
    // any parent re-render and cannot stand in for "the candidates changed". Clamping during
    // render keeps the row the user arrowed to, and keeps the highlight inside a shrunken list.
    const highlightedIndex = hasSuggestions
        ? Math.min(rawHighlightedIndex, suggestionList.length - 1)
        : -1;

    useLayoutEffect(() => {
        setCaret((current) => Math.min(current, value.length));
    }, [value]);

    const syncMirrorScroll = useCallback(() => {
        const input = inputRef.current;
        const mirror = mirrorRef.current;
        if (input && mirror) {
            mirror.style.transform = `translateX(${-input.scrollLeft}px)`;
        }
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: re-syncs on every value change, not on value itself
    useLayoutEffect(() => {
        syncMirrorScroll();
    }, [value, syncMirrorScroll]);

    const updateMenuPosition = useCallback(() => {
        const input = inputRef.current;
        if (!input) {
            return;
        }
        const caretOffsetPx = (measureRef.current?.offsetWidth ?? 0) - input.scrollLeft;
        setMenuPosition(
            computeCaretMenuGeometry({
                inputRect: input.getBoundingClientRect(),
                caretOffsetPx,
                menuWidth: menuRef.current?.offsetWidth ?? MENU_FALLBACK_WIDTH_PX,
                menuHeight: menuRef.current?.offsetHeight ?? 0,
                viewportWidth: window.innerWidth,
                viewportHeight: window.innerHeight,
            }),
        );
    }, []);

    // Depends on `caret` as well as `value`: the measuring span is sliced at the caret, so an
    // arrow-key move with no edit still changes where the menu belongs.
    // biome-ignore lint/correctness/useExhaustiveDependencies: repositions on every keystroke, not on value itself
    useLayoutEffect(() => {
        if (isMenuOpen) {
            updateMenuPosition();
        } else {
            setMenuPosition(null);
        }
        // `suggestions` and `loading` are here because they change the menu's own size: rows
        // arriving after a fetch resize it while the query and caret sit still, and a stale
        // measurement would leave `openUp` and the caret-aligned `left` computed for the old box.
    }, [isMenuOpen, value, caret, suggestions, loading, updateMenuPosition]);

    useEffect(() => {
        if (!isMenuOpen) {
            return undefined;
        }
        const handleScrollOrResize = () => updateMenuPosition();
        window.addEventListener('scroll', handleScrollOrResize, true);
        window.addEventListener('resize', handleScrollOrResize);
        const popEscapeLayer = pushEscapeLayer(() => setDismissed(true));
        return () => {
            window.removeEventListener('scroll', handleScrollOrResize, true);
            window.removeEventListener('resize', handleScrollOrResize);
            popEscapeLayer();
        };
    }, [isMenuOpen, updateMenuPosition]);

    // Restore the caret placed by the last accepted suggestion once the new `value` has
    // actually rendered. Guarded on the ref so an externally-driven value change never gets
    // its selection clobbered.
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally re-reads latest closures via pendingSelectionRef's gate
    useLayoutEffect(() => {
        const pending = pendingSelectionRef.current;
        if (pending === null) {
            return;
        }
        pendingSelectionRef.current = null;
        const input = inputRef.current;
        // Dropped unless the consumer applied exactly the string this caret was measured
        // against. A rejected or rewritten suggestion leaves the caret where the user put it.
        if (!input || pending.value !== value) {
            return;
        }
        input.setSelectionRange(pending.caret, pending.caret);
        setCaret(pending.caret);
        onQueryContextChange?.({ value, caret: pending.caret });
        syncMirrorScroll();
        updateMenuPosition();
    }, [value]);

    // Scrolled by hand rather than with `scrollIntoView`, which would also scroll the page and
    // carry the field itself out of view. Same reason MultiSelect does it this way.
    useLayoutEffect(() => {
        if (!rowsVisible) {
            return;
        }
        const row = rowRefs.current[highlightedIndex];
        const container = menuRef.current;
        if (!row || !container) {
            return;
        }
        const rowBottom = row.offsetTop + row.offsetHeight;
        if (rowBottom > container.scrollTop + container.clientHeight) {
            container.scrollTop = rowBottom - container.clientHeight;
        } else if (row.offsetTop < container.scrollTop) {
            container.scrollTop = row.offsetTop;
        }
    }, [highlightedIndex, rowsVisible]);

    // useInsertionEffect, not layout: the rules have to land before the rows paint.
    useInsertionEffect(() => {
        if (isMenuOpen) {
            ensureMultiSelectPanelStyles();
        }
    }, [isMenuOpen]);

    const reportCaret = (nextValue: string, nextCaret: number) => {
        setCaret(nextCaret);
        onQueryContextChange?.({ value: nextValue, caret: nextCaret });
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const next = event.target.value;
        // Editing is the user asking again, so it lifts an earlier Escape. Moving the caret is
        // not, and neither is a new `suggestions` array: that identity changes on any parent
        // re-render, which would undo the dismissal with nothing having been asked.
        setDismissed(false);
        setHighlightedIndex(0);
        onChange(next);
        reportCaret(next, event.target.selectionStart ?? next.length);
    };

    const handleSelect = (event: React.SyntheticEvent<HTMLInputElement>) => {
        const target = event.currentTarget;
        reportCaret(target.value, target.selectionStart ?? target.value.length);
    };

    const acceptSuggestion = (suggestion: QuerySuggestion) => {
        const fallbackCaret = inputRef.current?.selectionStart ?? value.length;
        const range = activeRange ?? { start: fallbackCaret, end: fallbackCaret };
        // Clamped for the same reason `sanitizeTokens` drops a bad range, except this one
        // writes: `slice` reads a negative index from the end of the string, so an unchecked
        // `start` of -1 silently rewrites the tail instead of doing nothing.
        const start = Math.max(0, Math.min(range.start, value.length));
        const end = Math.max(start, Math.min(range.end, value.length));
        const insertText = suggestion.insertText ?? suggestion.label;
        const next = value.slice(0, start) + insertText + value.slice(end);
        pendingSelectionRef.current = { value: next, caret: start + insertText.length };
        onChange(next);
        onAccept?.(suggestion);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (rowsVisible && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            event.preventDefault();
            const count = suggestionList.length;
            // Steps from the clamped index, so a list that shrank moves from the row on screen
            // rather than from a raw index the user can no longer see.
            setHighlightedIndex(
                event.key === 'ArrowDown'
                    ? (highlightedIndex + 1) % count
                    : (highlightedIndex - 1 + count) % count,
            );
            return;
        }

        const highlighted =
            rowsVisible && highlightedIndex >= 0 ? suggestionList[highlightedIndex] : undefined;

        if (event.key === 'Enter') {
            if (highlighted) {
                event.preventDefault();
                acceptSuggestion(highlighted);
                return;
            }
            if (onSubmit) {
                // Otherwise a surrounding <form> submits too and the query is sent twice.
                // Without the prop the native submit is left alone, which is the useful default.
                event.preventDefault();
                onSubmit(value);
            }
            return;
        }

        if (event.key === 'Tab' && highlighted) {
            // Tab completes the term and stays put, the way an editor or a search bar does.
            // Letting focus move on would hand the next keystroke to another field.
            event.preventDefault();
            acceptSuggestion(highlighted);
        }
    };

    const segments = buildSegments(value, tokens);
    const textStyle = TEXT_STYLE[size];

    const menuContent = (() => {
        if (loading) {
            return (
                // `role="listbox"` admits only option/group children.
                <div role="presentation" style={MENU_MESSAGE_STYLE}>
                    <Spinner size="xxxsmall" />
                    <span>Loading…</span>
                </div>
            );
        }
        if (hasSuggestions) {
            return suggestionList.map((suggestion, index) => {
                const highlighted = index === highlightedIndex;
                return (
                    <button
                        key={suggestion.id}
                        ref={(element) => {
                            rowRefs.current[index] = element;
                        }}
                        type="button"
                        id={getRowId(index)}
                        role="option"
                        aria-selected={highlighted}
                        // Focus stays on the input under the activedescendant model, so a row
                        // must not become its own tab stop.
                        tabIndex={-1}
                        onClick={() => acceptSuggestion(suggestion)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        className="malachite-multiselect-option"
                        data-highlighted={highlighted}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            width: '100%',
                            flexShrink: 0,
                            border: 'none',
                            textAlign: 'left',
                            borderRadius: 8,
                            padding: '8px 10px',
                            font: 'inherit',
                            cursor: 'pointer',
                            color: 'var(--malachite-text-primary, rgb(10, 10, 10))',
                        }}
                    >
                        <span
                            style={{
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {suggestion.label}
                        </span>
                        {suggestion.detail && (
                            <span
                                style={{
                                    flexShrink: 0,
                                    fontSize: 'var(--malachite-font-size-xsmall, 11px)',
                                    color: 'var(--malachite-text-secondary, rgb(123, 123, 123))',
                                }}
                            >
                                {suggestion.detail}
                            </span>
                        )}
                    </button>
                );
            });
        }
        if (emptyMessage) {
            // `role="listbox"` admits only option/group children.
            return (
                <div role="presentation" style={MENU_MESSAGE_STYLE}>
                    {emptyMessage}
                </div>
            );
        }
        return null;
    })();

    const hoverBorderClass = disabled ? '' : 'hover:border-[var(--malachite-text-placeholder)]';

    return (
        <div
            className={`relative w-full ${SIZE_CLASSES[size]} bg-[var(--malachite-card-background)] border border-solid border-[var(--malachite-card-border)] ${hoverBorderClass} transition-colors flex items-center ${disabled ? 'opacity-60 cursor-not-allowed' : ''} ${className}`}
            style={{
                boxShadow: focused
                    ? 'var(--malachite-shadow-accent, 0px 1px 0px 0px rgba(0, 175, 102, 0.15))'
                    : '0px 1px 1px 0px rgba(0, 0, 0, 0.15)',
                borderColor: focused
                    ? 'var(--malachite-control-border-hover, rgba(0, 175, 102, 0.4))'
                    : undefined,
                ...style,
            }}
        >
            {/* Clips here, not on the mirror: the mirror is what translates, and a transform
                carries the element's own clip rect with it, so the window onto the text would
                slide out of the field by exactly `scrollLeft`. */}
            <div className="relative flex-1 h-full min-w-0 overflow-hidden">
                {/* aria-hidden: purely decorative, the input carries the accessible value. */}
                <div
                    ref={mirrorRef}
                    aria-hidden="true"
                    className={`absolute inset-0 flex items-center pointer-events-none ${CONTROL_TEXT_SIZE_CLASS[size]}`}
                    style={{ ...textStyle, whiteSpace: 'pre' }}
                >
                    {segments.map((segment) => (
                        <span
                            key={segment.start}
                            style={{
                                color: TOKEN_COLORS[segment.kind],
                                ...(segment.kind === 'error'
                                    ? { textDecoration: 'underline wavy' }
                                    : {}),
                            }}
                        >
                            {value.slice(segment.start, segment.end)}
                        </span>
                    ))}
                    <span
                        ref={measureRef}
                        style={{ position: 'absolute', top: 0, left: 0, visibility: 'hidden' }}
                    >
                        {value.slice(0, caret)}
                    </span>
                </div>
                <input
                    ref={inputRef}
                    type="text"
                    role="combobox"
                    aria-expanded={isMenuOpen}
                    aria-controls={isMenuOpen ? listboxId : undefined}
                    aria-activedescendant={
                        rowsVisible && highlightedIndex >= 0
                            ? getRowId(highlightedIndex)
                            : undefined
                    }
                    aria-autocomplete="list"
                    autoComplete="off"
                    spellCheck={false}
                    aria-labelledby={ariaLabelledBy}
                    // A visible label outranks it, so the fallback only applies without one.
                    aria-label={ariaLabelledBy ? undefined : (ariaLabel ?? placeholder)}
                    value={value}
                    onChange={handleChange}
                    onSelect={handleSelect}
                    onScroll={() => {
                        syncMirrorScroll();
                        if (isMenuOpen) {
                            updateMenuPosition();
                        }
                    }}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                        setFocused(true);
                        setDismissed(false);
                    }}
                    onBlur={() => setFocused(false)}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={`absolute inset-0 w-full h-full bg-transparent border-none outline-none ${CONTROL_TEXT_SIZE_CLASS[size]} placeholder:text-[var(--malachite-text-placeholder)] placeholder:font-medium disabled:cursor-not-allowed`}
                    style={{
                        ...textStyle,
                        color: 'transparent',
                        caretColor: 'var(--malachite-text-primary, rgb(10, 10, 10))',
                    }}
                />
            </div>
            {isMenuOpen &&
                createPortal(
                    <div
                        ref={menuRef}
                        id={listboxId}
                        role="listbox"
                        // Rides the dropdown overlay band, but borrows the multiselect option
                        // rows below: their highlight follows `data-highlighted`, which is the
                        // keyboard cursor this menu has, rather than a bare `:hover`.
                        {...overlayIdentity('dropdown')}
                        onMouseDown={handleMenuMouseDown}
                        style={{
                            position: 'fixed',
                            top: menuPosition?.top ?? 0,
                            left: menuPosition?.left ?? 0,
                            minWidth: Math.min(
                                MENU_FALLBACK_WIDTH_PX,
                                menuPosition?.width ?? MENU_FALLBACK_WIDTH_PX,
                            ),
                            maxWidth: Math.min(
                                MENU_MAX_WIDTH_PX,
                                menuPosition?.width ?? MENU_MAX_WIDTH_PX,
                            ),
                            background: 'var(--malachite-card-background, rgb(255, 255, 255))',
                            border: '1px solid var(--malachite-card-border, rgb(232, 232, 232))',
                            borderRadius: 12,
                            boxShadow: '0px 2px 5px 0px rgba(0, 0, 0, 0.1)',
                            padding: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            maxHeight: 320,
                            overflowY: 'auto',
                            fontSize: CONTROL_TEXT_SIZE[size],
                            visibility: menuPosition ? 'visible' : 'hidden',
                            opacity: menuPosition ? 1 : 0,
                        }}
                    >
                        {menuContent}
                    </div>,
                    document.body,
                )}
        </div>
    );
};

export default QueryInput;
export { QueryInput };
